import type {TextStyleRunObject} from '@PosterWhiteboard/items/text-item/text-style-runs.types';

/**
 * Why a text item with per-selection or per-line styling has to be rasterized for the vector PDF
 * path instead of going through `toSVG()`. Fabric exports style runs as one `tspan` per style chunk,
 * the fork's line offsets honor per-line alignment, and since 7.1.0-pmw-70 the export carries list
 * markers (the bullets face is embedded with the other fonts) and per-character gradient
 * definitions, so those stay vector; the reasons below are the gaps the SVG export still has.
 */
export enum TextPdfRasterizeReason {
  /** svg-to-pdfkit paints a stroke over the fill, so an outlined glyph loses its fill; the whole-box outline rasterizes for the same reason. */
  RUN_OUTLINE = 'run-outline',
  /** Fabric exports shadows at object level only; a run's shadow would be dropped. */
  RUN_SHADOW = 'run-shadow',
  /** Same rules as the whole box: fabric breaks decorations per character under letter spacing, and underline plus line-through together. */
  RUN_DECORATIONS = 'run-decorations',
}

export interface TextPdfSafetyBaseStyles {
  letterSpacing: number;
  underLine: boolean;
  lineThrough: boolean;
}

export interface TextPdfSafetyInput {
  styleRuns: TextStyleRunObject[];
  baseStyles: TextPdfSafetyBaseStyles;
}

/** `null` when the item's rich-text styling survives the SVG export and the item may stay vector in the PDF. */
export const getRichTextPdfRasterizeReason = ({styleRuns, baseStyles}: TextPdfSafetyInput): TextPdfRasterizeReason | null => {
  if (styleRuns.some((run) => run.styles.outline?.isEnabled === true)) {
    return TextPdfRasterizeReason.RUN_OUTLINE;
  }
  if (styleRuns.some((run) => run.styles.shadow !== null)) {
    return TextPdfRasterizeReason.RUN_SHADOW;
  }
  if (styleRuns.some((run) => hasIncompatibleDecorations(run, baseStyles))) {
    return TextPdfRasterizeReason.RUN_DECORATIONS;
  }
  return null;
};

const hasIncompatibleDecorations = (run: TextStyleRunObject, baseStyles: TextPdfSafetyBaseStyles): boolean => {
  const underLine = run.styles.underLine ?? baseStyles.underLine;
  const lineThrough = run.styles.lineThrough ?? baseStyles.lineThrough;
  const hasDecoration = underLine || lineThrough;
  return (baseStyles.letterSpacing > 0 && hasDecoration) || (underLine && lineThrough);
};
